import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, setAdminToken } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Bell, Loader2, Check, Trash2, ChevronRight, ClipboardCheck, Package, AlertTriangle, Wrench, Ban } from "lucide-react";
import { toast } from "sonner";

const ICONS = {
  booking: ClipboardCheck,
  order: Package,
  low_stock: AlertTriangle,
  quote_approved: Check,
  system: Wrench,
  flagged_booking: Ban,
};

const ICON_COLOURS = {
  booking: "text-spark-teal bg-spark-teal/15",
  order: "text-spark-yellow bg-spark-yellow/15",
  low_stock: "text-orange-400 bg-orange-500/15",
  quote_approved: "text-emerald-400 bg-emerald-500/15",
  system: "text-slate-400 bg-white/5",
  flagged_booking: "text-red-400 bg-red-500/15",
};

const AdminNotifications = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/notifications", { params: { unread_only: filter === "unread" } });
      setItems(res.data.items);
      setUnread(res.data.unread_count);
    } catch (err) {
      // Ignore aborted/cancelled requests (e.g. when user navigates away)
      if (err.code === "ERR_CANCELED" || err.message === "canceled") return;
      if (err.response?.status === 401) {
        setAdminToken(null);
        navigate("/admin/login");
      } else toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const markAll = async () => {
    try {
      await api.post("/admin/notifications/mark-all-read");
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch { toast.error("Failed"); }
  };

  const markOne = async (id) => {
    // Optimistic — update UI immediately, fire-and-forget the API call.
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
    api.patch(`/admin/notifications/${id}`).catch(() => {});
  };

  const remove = async (id) => {
    setItems((prev) => prev.filter((n) => n.id !== id));
    try { await api.delete(`/admin/notifications/${id}`); }
    catch { toast.error("Delete failed"); load(); }
  };

  const handleClick = (n) => {
    if (!n.read) markOne(n.id);
    if (n.link) navigate(n.link);
  };

  return (
    <AdminLayout
      eyebrow="Workshop signals"
      title="Inbox"
      actions={
        unread > 0 ? (
          <button onClick={markAll} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-spark-yellow text-spark-ink text-xs uppercase tracking-wider font-bold hover:bg-spark-yellow-dark" data-testid="mark-all-read">
            <Check className="size-3.5" /> Mark all read
          </button>
        ) : null
      }
    >
      <div className="space-y-4">
        <div className="flex gap-2">
          {["all", "unread"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3.5 py-1.5 rounded-full text-xs uppercase tracking-wider font-bold border transition-colors ${filter === f ? "bg-spark-yellow text-spark-ink border-spark-yellow" : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"}`}
              data-testid={`notif-filter-${f}`}
            >
              {f === "unread" && unread > 0 ? `Unread · ${unread}` : f}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20 text-slate-400"><Loader2 className="size-5 animate-spin inline" /></div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-white/[0.07] bg-white/[0.04] py-16 text-center">
            <Bell className="size-10 text-slate-500 mx-auto mb-3" />
            <p className="text-slate-300 font-display uppercase tracking-wider">All clear</p>
            <p className="text-sm text-slate-500 mt-1">New bookings, orders, and stock alerts will land here.</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {items.map((n) => {
              const Icon = ICONS[n.type] || Bell;
              const colour = ICON_COLOURS[n.type] || "text-slate-400 bg-white/5";
              return (
                <li key={n.id} className={`group rounded-xl border ${n.read ? "border-white/[0.05] bg-white/[0.025]" : "border-spark-yellow/30 bg-spark-yellow/[0.04]"} transition-colors`}>
                  <button
                    type="button"
                    onClick={() => handleClick(n)}
                    className="flex w-full items-start gap-3 p-4 text-left hover:bg-white/[0.03] transition-colors cursor-pointer"
                    data-testid={`notif-${n.id}`}
                  >
                    <div className={`size-9 rounded-md flex items-center justify-center shrink-0 ${colour}`}>
                      <Icon className="size-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold ${n.read ? "text-slate-200" : "text-white"} truncate`}>{n.title}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{n.body}</p>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-1.5">
                        {new Date(n.created_at).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" })}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); remove(n.id); }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); remove(n.id); } }}
                        className="p-1.5 rounded-md text-slate-500 hover:text-red-400 hover:bg-white/5 opacity-0 group-hover:opacity-100 transition cursor-pointer"
                        title="Delete"
                        data-testid={`notif-delete-${n.id}`}
                      >
                        <Trash2 className="size-3.5" />
                      </span>
                      {n.link && <ChevronRight className="size-4 text-slate-500" />}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminNotifications;
